/**
 * SUP-ADMIN-UI-01 — small fetch helper for the `support/v1` namespace.
 *
 * Mirrors `tenancyFetch.ts`, with one deliberate difference: the triage
 * read / patch endpoints are **platform-admin, cross-tenant** calls, so this
 * helper sends ONLY the bearer token — never an `X-Tenant-ID` header. Triage
 * is inherently cross-tenant (support sees every tenant's queue); the optional
 * `tenantId` query filter narrows by attribution instead (contract D2).
 *
 * Base URL comes from `getSupportBaseUrl()` so `DEV_API_MODE`
 * (realbe / devapi-*) routes requests correctly. Errors carry the parsed JSON
 * body so the UI can surface the BE error envelopes (`code`, `reason`,
 * `message`).
 */
import { getSupportBaseUrl, useAuthStore } from "@aeriosiq/auth-client";

export interface SupportFetchError extends Error {
  status: number;
  body: unknown;
}

interface SupportFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

function authHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  // Deliberately NO X-Tenant-ID — triage is platform-admin + cross-tenant.
  return headers;
}

export async function supportFetch<T>(
  path: string,
  opts: SupportFetchOptions = {},
): Promise<T> {
  const headers = authHeaders();
  let bodyInit: BodyInit | undefined;
  if (opts.body !== undefined) {
    bodyInit = JSON.stringify(opts.body);
    headers.set("Content-Type", "application/json");
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (bodyInit !== undefined) {
    init.body = bodyInit;
  }
  const res = await fetch(`${getSupportBaseUrl()}${path}`, init);
  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // Non-JSON error body — status still surfaces.
    }
    const err = new Error(
      `support fetch failed: ${path} → ${String(res.status)}`,
    ) as SupportFetchError;
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  const text = await res.text();
  if (text === "") return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as unknown as T;
  }
}
